import React, { useEffect, useState } from 'react'
import { Table, Spin, message, Tag, Tooltip, Icon, Popconfirm, Button, Modal, Input } from 'antd'
import { Link } from 'react-router-dom'
import moment from 'moment'
import axios from '../../util/Api'

function TrackList () {
  const [liveTracks, setLiveTracks] = useState([])
  const [assetCount, setAssetCount] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  })
  const [urlModalVisible, setUrlModalVisible] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)

  useEffect(() => {
    fetchLiveTracks()
  }, [])

  const fetchLiveTracks = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/live-track')
      if (response && response.data) {
        setAssetCount(response.data.assetCount || 0)
        setTotal(response.data.total || 0)
        setLiveTracks(response.data.liveTracks || [])
      }
    } catch (err) {
      console.error('Error fetching live tracks:', err)
      message.error('Failed to fetch live tracks')
    } finally {
      setLoading(false)
    }
  }

  const getExpiryStatus = (expiryTimestamp) => {
    if (!expiryTimestamp) return { status: 'unknown', color: '#8c8c8c' }
    const expiryTime = moment(expiryTimestamp)
    const now = moment()
    const duration = moment.duration(expiryTime.diff(now))

    if (duration.asMinutes() <= 0) {
      return { status: 'Expired', color: '#f5222d' }
    } else if (duration.asHours() < 24) {
      return { status: 'Expiring Soon', color: '#faad14' }
    }
    return { status: 'Active', color: '#52c41a' }
  }

  const handleDelete = async (record) => {
    if (!record || !record.token) {
      message.error('Invalid record')
      return
    }
    try {
      setLoading(true)
      await axios.delete(`/live-track/delete-token/${record.token}`)
      message.success('Track deleted successfully')
      fetchLiveTracks()
    } catch (err) {
      console.error('Error deleting track:', err)
      message.error('Failed to delete track')
      setLoading(false)
    }
  }

  const getShareUrl = (record) => {
    if (!record || !record.token || !record.asset) return ''
    const assetId = record.asset._id
    const device = record.imei || ''
    const token = record.token
    const hostname = window.location.hostname

    // If localhost, use the standard path
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      const baseUrl = window.location.origin
      return `${baseUrl}/public/track/${assetId}/${device}/${token}`
    }

    // For non-localhost, add 'track' as subdomain
    const protocol = window.location.protocol
    const domainParts = hostname.split('.')
    const mainDomain = domainParts.length > 2
      ? domainParts.slice(-2).join('.')
      : hostname
    return `${protocol}//track.${mainDomain}/public/track/${assetId}/${device}/${token}`
  }

  const handleViewUrl = (record) => {
    setSelectedRecord(record)
    setUrlModalVisible(true)
  }

  const handleCopyUrl = async () => {
    if (!selectedRecord) return
    const url = getShareUrl(selectedRecord)
    if (!url) {
      message.error('Unable to generate URL')
      return
    }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url)
        message.success('URL copied to clipboard!')
      } else {
        const input = document.createElement('input')
        input.value = url
        input.style.position = 'fixed'
        input.style.opacity = '0'
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        message.success('URL copied to clipboard!')
      }
    } catch (err) {
      console.error('Failed to copy URL:', err)
      message.error('Failed to copy URL')
    }
  }

  const columns = [
    {
      title: 'Sl.',
      dataIndex: 'sl',
      key: 'sl',
      render: (text, record, index) => {
        return <span>{(pagination.current - 1) * pagination.pageSize + index + 1}</span>
      },
      width: 60
    },
    {
      title: 'Asset',
      dataIndex: 'asset',
      key: 'asset',
      render: (asset, record) => {
        if (!asset) return '-'
        const assetId = asset._id
        const deviceId = record.imei || ''
        return (
          <Link target='_blank' to={`/iot/device-details/${assetId}/${deviceId}`}>
            <span>{asset.assetCode + ' - ' + asset.ownerCode + ' - ' + asset.altName}</span>
          </Link>
        )
      },
      sorter: (a, b) => {
        const codeA = a.asset && a.asset.assetCode || ''
        const codeB = b.asset && b.asset.assetCode || ''
        return codeA.localeCompare(codeB)
      }
    },
    {
        title: 'Asset Sub Type',
        dataIndex: 'asset',
        key: 'assetSubType',
        render: (asset) => {
            if (!asset) return '-'
            return <span>{asset.equipmentSubType && asset.equipmentSubType.name || '-'}</span>
        },
        sorter: (a, b) => {
            const typeA = a.asset && a.asset.equipmentSubType && a.asset.equipmentSubType.name || ''
            const typeB = b.asset && b.asset.equipmentSubType && b.asset.equipmentSubType.name || ''
            return typeA.localeCompare(typeB)
        }
    },
    { title: 'Asset Model',
        dataIndex: 'asset',
        key: 'assetModel',
        render: (asset) => {
            if (!asset) return '-'
            return <span>{asset.assetModel && asset.assetModel.name || '-'}</span>
        },
        sorter: (a, b) => {
            const modelA = a.asset && a.asset.assetModel && a.asset.assetModel.name || ''
            const modelB = b.asset && b.asset.assetModel && b.asset.assetModel.name || ''
            return modelA.localeCompare(modelB)
        }
    },
    {
        title: 'Location',
        dataIndex: 'asset',
        key: 'location',
        render: (asset) => {
            if (!asset) return '-'
            return <span>{asset.location && asset.location.name || '-'}</span>
        },
        sorter: (a, b) => {
            const locationA = a.asset && a.asset.location && a.asset.location.name || ''
            const locationB = b.asset && b.asset.location && b.asset.location.name || ''
            return locationA.localeCompare(locationB)
        }
    },
    {
      title: 'Created By',
      dataIndex: 'user',
      key: 'user',
      render: (user) => {
        if (!user) return '-'
        return (
          <Tooltip title={user.email || ''}>
            <span>{user.name || user.email || user._id || '-'}</span>
          </Tooltip>
        )
      }
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt) => {
        if (!createdAt) return '-'
        return <span>{moment(createdAt).format('DD MMM YYYY, hh:mm A')}</span>
      },
      sorter: (a, b) => {
        const timeA = moment(a.createdAt)
        const timeB = moment(b.createdAt)
        return timeA.valueOf() - timeB.valueOf()
      }
    },
    {
      title: 'Expiry',
      dataIndex: 'expiryTimestamp',
      key: 'expiryTimestamp',
      render: (expiryTimestamp) => {
        if (!expiryTimestamp) return '-'
        const { status, color } = getExpiryStatus(expiryTimestamp)
        return (
          <div>
            <Tag color={color}>{status}</Tag>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
              {moment(expiryTimestamp).format('DD MMM YYYY, hh:mm A')}
            </div>
          </div>
        )
      },
      sorter: (a, b) => {
        const timeA = moment(a.expiryTimestamp)
        const timeB = moment(b.expiryTimestamp)
        return timeA.valueOf() - timeB.valueOf()
      }
    },
    // {
    //   title: 'Token',
    //   dataIndex: 'token',
    //   key: 'token',
    //   render: (token) => {
    //     if (!token) return '-'
    //     const truncatedToken = token.length > 12 ? `${token.substring(0, 12)}...` : token
    //     return (
    //       <Tooltip title={token}>
    //         <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{truncatedToken}</span>
    //       </Tooltip>
    //     )
    //   },
    //   width: 140
    // },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (text, record) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Tooltip title='View Share URL'>
              <Icon
                type='eye'
                style={{ fontSize: 18, cursor: 'pointer', color: '#1890ff' }}
                onClick={() => handleViewUrl(record)}
              />
            </Tooltip>
            <Popconfirm
              title='Are you sure you want to delete this tracking URL?'
              onConfirm={() => handleDelete(record)}
              okText='Yes'
              cancelText='No'
              okType='danger'
            >
              <Tooltip title='Delete'>
                <Icon
                  type='delete'
                  style={{ fontSize: 18, cursor: 'pointer', color: '#f5222d' }}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        )
      },
      width: 100
    }
  ]

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>
          <Icon type='share-alt' style={{ marginRight: 8 }} />
          Live Tracking URLs
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Tag color='blue'>
            <Icon type='car' style={{ marginRight: 4 }} />
            Total Assets: {assetCount}
          </Tag>
          <Tag color='green'>
            <Icon type='link' style={{ marginRight: 4 }} />
            Active Tracks: {total}
          </Tag>
          <Tooltip title='Refresh'>
            <Icon
              type='reload'
              style={{ fontSize: 18, cursor: 'pointer', color: '#1890ff' }}
              spin={loading}
              onClick={fetchLiveTracks}
            />
          </Tooltip>
        </div>
      </div>

      <Spin spinning={loading}>
        <Table
          dataSource={liveTracks}
          columns={columns}
          bordered
          size='small'
          rowKey={(record) => record._id}
          pagination={{
            size: 'small',
            total: total,
            pageSize: pagination.pageSize,
            current: pagination.current,
            showTotal: (totalRecords, range) => `${range[0]}-${range[1]} of ${totalRecords} items`,
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize })
            }
          }}
        />
      </Spin>

      <Modal
        title='Share Tracking URL'
        visible={urlModalVisible}
        onCancel={() => {
          setUrlModalVisible(false)
          setSelectedRecord(null)
        }}
        footer={[
          <Button key='cancel' onClick={() => {
            setUrlModalVisible(false)
            setSelectedRecord(null)
          }}>
            Close
          </Button>,
          <Button key='copy' type='primary' icon='copy' onClick={handleCopyUrl}>
            Copy URL
          </Button>
        ]}
        width={600}
      >
        {selectedRecord && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>Asset:</strong> {selectedRecord.asset && selectedRecord.asset.assetCode || '-'}
              {selectedRecord.asset && selectedRecord.asset.altName && ` (${selectedRecord.asset.altName})`}
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Shareable URL:</strong>
            </div>
            <Input.TextArea
              value={getShareUrl(selectedRecord)}
              readOnly
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </div>
        )}
      </Modal>
    </div>
  )
}

export default TrackList

